import { ScrollableCheckboxList, type ScrollableCheckboxListOption } from '@faceup/ui'
import { Skeleton, Typography } from '@faceup/ui-base'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useMemo } from 'react'
import { defineMessages, FormattedMessage, useIntl } from '../../../TypedIntl'
import type { fragments } from '../AF_CreateReportForm'
import type { Recipient } from '../hooks/useRecipients'
import { RecipientPopoverContent } from './RecipientPopoverContent'

const messages = defineMessages({
  recipientsCantBeShown: 'FollowUp.report.recipientsCantBeShown',
  recipientsSelectCategoryAndOrganisationUnitFirst:
    'FollowUp.report.recipientsSelectCategoryAndOrganisationUnitFirst',
  recipientsSelectOrganizationUnitFirst: 'FollowUp.report.recipientsSelectOrganisationUnitFirst',
  recipientsAriaLabel: 'FollowUp.report.recipients',
})

type AF_RecipientsProps = {
  selectedOrganizationUnit?: string
  selectedCategory?: string
  recipients: Recipient[]
  changeRecipient: (member: Recipient) => void
  areRecipientsLoading: boolean
  institution: ResultOf<typeof fragments.AF_CreateReportForm_institution>
  hintId?: string
}

export const AF_Recipients = ({
  selectedOrganizationUnit,
  selectedCategory,
  recipients,
  changeRecipient,
  areRecipientsLoading,
  institution,
  hintId,
}: AF_RecipientsProps) => {
  const { formatMessage } = useIntl()

  const partnerName = institution?.partner?.name ?? null
  const institutionName = institution?.config?.institutionName ?? null

  const options = useMemo<ScrollableCheckboxListOption[]>(
    () =>
      recipients.map(recipient => ({
        id: recipient.id,
        label: recipient.name,
        disabled: !recipient.isEditable,
        popoverContent: (
          <RecipientPopoverContent
            recipient={recipient}
            companyName={recipient.isPartner ? partnerName : institutionName}
          />
        ),
      })),
    [recipients, partnerName, institutionName]
  )

  const selectedIds = useMemo(
    () => recipients.filter(r => r.isSelected).map(r => r.id),
    [recipients]
  )

  if (!selectedOrganizationUnit || !selectedCategory) {
    if (selectedOrganizationUnit) {
      return (
        <Typography.Text type='secondary'>
          <FormattedMessage {...messages.recipientsCantBeShown} />
        </Typography.Text>
      )
    }
    if (selectedCategory) {
      return (
        <Typography.Text type='secondary'>
          <FormattedMessage {...messages.recipientsSelectOrganizationUnitFirst} />
        </Typography.Text>
      )
    }
    return (
      <Typography.Text type='secondary'>
        <FormattedMessage {...messages.recipientsSelectCategoryAndOrganisationUnitFirst} />
      </Typography.Text>
    )
  }

  if (areRecipientsLoading) {
    return <Skeleton.Avatar />
  }

  return (
    <ScrollableCheckboxList
      aria-describedby={hintId}
      aria-label={formatMessage(messages.recipientsAriaLabel)}
      options={options}
      selectedIds={selectedIds}
      onChange={id => {
        const recipient = recipients.find(r => r.id === id)
        if (recipient) {
          changeRecipient(recipient)
        }
      }}
    />
  )
}
